/* Shared white product purchase presentation. Native Shopify/Kaching controls own all commerce. */
(() => {
// Shopify's rendered amounts can use different separators from the page language.
// Keep Liquid/Cart API's x100 units; unknown or rounded formats stay native.
function createAppMoney(config, currency) {
  if (config?.marketCurrencyCode !== currency || typeof config.moneyFormat !== 'string') return null;
  let digits;
  try { digits = new Intl.NumberFormat('en', {style:'currency', currency}).resolvedOptions().maximumFractionDigits; }
  catch { return null; }
  if (![0, 2].includes(digits)) return null;
  const normalize = text => text.replace(/[\u200e\u200f\u061c\u202a-\u202e\u2066-\u2069]/g, '').replace(/[\u00a0\u202f]/g, ' ').replace(/\u2019/g, "'");
  const template = normalize(config.moneyFormat).replace(/{{\s*currency\s*}}/g, currency);
  const tokens = [...template.matchAll(/{{\s*(\w+)\s*}}/g)];
  if (tokens.length !== 1 || /[<>]/.test(template)) return null;
  const formats = {
    amount:[',', '.', 2], amount_with_comma_separator:['.', ',', 2],
    amount_with_apostrophe_separator:["'", '.', 2], amount_with_space_separator:[' ', ',', 2],
    amount_with_period_and_space_separator:[' ', '.', 2], amount_no_decimals:[',', '.', 0],
    amount_no_decimals_with_comma_separator:['.', ',', 0], amount_no_decimals_with_space_separator:[' ', ',', 0],
  };
  const settings = formats[tokens[0][1]];
  if (!settings || (settings[2] === 0 && digits !== 0)) return null;
  const [group, decimal, precision] = settings;
  const prefix = template.slice(0, tokens[0].index);
  const suffix = template.slice(tokens[0].index + tokens[0][0].length);
  return {
    parse(value) {
      if (typeof value !== 'string') return null;
      const text = normalize(value).trim();
      if (!text.startsWith(prefix) || !text.endsWith(suffix)) return null;
      const amount = text.slice(prefix.length, suffix ? -suffix.length : undefined);
      const parts = amount.split(decimal);
      if (parts.length > 2 || (precision === 0 && parts.length !== 1)) return null;
      const fraction = parts[1] || '';
      if (precision && (parts.length !== 2 || !/^\d{2}$/.test(fraction))) return null;
      if (digits === 0 && fraction && fraction !== '00') return null;
      const groups = parts[0].split(group);
      if (!/^\d+$/.test(groups[0]) || (groups.length > 1 &&
          (groups[0].length > 3 || groups.slice(1).some(part => !/^\d{3}$/.test(part))))) return null;
      const result = Number(groups.join('')) * 100 + Number(fraction);
      return Number.isSafeInteger(result) ? result : null;
    },
    format(value) {
      const [whole, fraction] = (value / 100).toFixed(precision).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, group);
      return prefix + grouped + (precision ? decimal + fraction : '') + suffix;
    },
  };
}
// App attributes can arrive or change after initialization. Cache only their
// exact current source; native DOM prices and selected controls stay live.
function createAppDataReader() {
  const attributes = new WeakMap();
  let moneyConfig, moneyCurrency, moneyCodec;
  const json = (block, name) => {
    const raw = block.getAttribute(name);
    let cache = attributes.get(block);
    if (!cache) { cache = new Map(); attributes.set(block, cache); }
    const previous = cache.get(name);
    if (previous && previous.raw === raw) return previous.value;
    // An invalid new value must throw into the existing native fallback,
    // never return a previously valid offer or currency configuration.
    const value = JSON.parse(raw);
    cache.set(name, {raw, value});
    return value;
  };
  return {
    json,
    money(block, currency) {
      const config = json(block, 'config');
      if (config !== moneyConfig || currency !== moneyCurrency) {
        moneyCodec = createAppMoney(config, currency);
        moneyConfig = config;
        moneyCurrency = currency;
      }
      return moneyCodec;
    },
  };
}
// Mirror the app's displayed prices; this never calculates discounts or edits a cart.
// If its markup or currency format is unrecognized, omit the mirror rather than guess.
const purchase = document.querySelector(".mz-purchase");
if (!purchase || !document.body.classList.contains('mz-light')) return;
let product;
try { product = JSON.parse(purchase.querySelector('[data-mz-product]')?.textContent); } catch { return; }
if (!product?.variants?.length) return;
const appData = createAppDataReader();
const glass = product.collections?.includes('glassware') || (/glass|tumbler/i.test(product.title) && !/base/i.test(product.title));
const unit = glass ? 'glass' : 'item';
const plural = glass ? 'glasses' : 'items';
const unitTitle = glass ? 'Glass' : 'Item';
let initialPurchaseShown = false;
const revealPurchase = () => {
  if (initialPurchaseShown) return;
  initialPurchaseShown = true;
  document.dispatchEvent(new CustomEvent('mz:purchase-ready'));
};
const toggleClass = (node, name, value) => {
  if (node.classList.contains(name) !== !!value) node.classList.toggle(name, !!value);
};
// Only the main product's offers belong to the quantity chooser. Other app
// bars may be independent checkbox add-ons (for example Swirl's decanter).
const mainBars = block => [...(block?.querySelectorAll('.kaching-bundles__bar') || [])]
  .filter(bar => bar.dataset.dealBarId && !bar.closest('.kaching-bundles__checkbox-upsells') && bar.closest('kaching-bundles-block') === block);
const appMatchesProduct = block => {
  try { return String(appData.json(block, 'product').id).split('/').pop() === String(product.id); }
  catch { return false; }
};
if (purchase) {
  // Native offers collapse their old options above the newly clicked header.
  // Preserve that header's screen position after the app and our list styling
  // have rendered, without changing its selection or moving any app controls.
  let offerAnchor = null;
  const cancelOfferAnchor = () => { offerAnchor = null; };
  const anchorOffer = (event) => {
    if (purchase.classList.contains('mz-set-selector-ready')) return;
    if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
    if (event.type === 'click' && event.button !== 0) return;
    const header = event.target.closest?.('.kaching-bundles__bar-main');
    const bar = header?.closest('.kaching-bundles__bar');
    if (!bar || bar.classList.contains('kaching-bundles__bar--selected')) return;
    const bounds = header.getBoundingClientRect();
    const top = Math.max(16, Math.min(bounds.top, window.innerHeight - bounds.height - 24));
    const anchor = { header, bar, top };
    offerAnchor = anchor;
    // Kaching renders first; the existing presentation observer runs next.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (offerAnchor !== anchor) return;
      offerAnchor = null;
      if (!anchor.header.isConnected || !anchor.bar.classList.contains('kaching-bundles__bar--selected')) return;
      const shift = anchor.header.getBoundingClientRect().top - anchor.top;
      if (Math.abs(shift) > 1) window.scrollBy({top:shift, behavior:'instant'});
    }));
  };
  purchase.addEventListener('click', anchorOffer, true);
  purchase.addEventListener('keydown', anchorOffer, true);
  // A deliberate scroll takes priority over the pending correction.
  window.addEventListener('wheel', cancelOfferAnchor, {passive:true});
  window.addEventListener('touchmove', cancelOfferAnchor, {passive:true});
}
if (purchase) {
  purchase.classList.add('mz-product-purchase');
  // Size the native offer layout without CSS containment, which would change
  // the app's viewport-positioned dropdown menu coordinates.
  const sizeOffers = (width) => toggleClass(purchase, 'mz-compact-offers', width <= 400);
  sizeOffers(purchase.clientWidth);
  new ResizeObserver(([entry]) => sizeOffers(entry.contentRect.width)).observe(purchase);
}
// Style the native Kaze selectors as a visible list. No selection state or
// submission gate: every glass remains directly editable through Kaching.
const styleGlassList = (bar) => {
  const root = bar.querySelector('.kaching-bundles__bar-variants');
  if (!root || root.closest('.kaching-bundles__upsell')) return;
  const rows = [...root.children].filter(node => node.matches('.kaching-bundles__bar-variant-selector-wrapper'));
  const eligible = rows.length > 0 && rows.every(node => node.querySelectorAll('[role="combobox"],select').length === 1);
  toggleClass(root, 'mz-glass-list', eligible);
  const nativeSelects = eligible && rows.every(row => row.querySelector('select'));
  toggleClass(root, 'mz-native-choice-list', nativeSelects);
  root.dataset.mzGlassCount = String(rows.length);
  if (nativeSelects) rows.forEach((row, index) => {
    let number = row.querySelector('.mz-native-choice-number');
    if (!number) {
      number = document.createElement('span');
      number.className = 'mz-native-choice-number';
      row.querySelector('.kaching-bundles__bar-variant-selects').before(number);
    }
    const label = `${unitTitle} ${index + 1}`;
    if (number.textContent !== label) number.textContent = label;
  });
  toggleClass(bar, 'mz-list-bundle', eligible);
  let title = root.querySelector('.mz-glass-list__title');
  if (!eligible) { title?.remove(); return; }
  if (!title) {
    title = document.createElement('h3');
    title.className = 'mz-glass-list__title';
    root.prepend(title);
  }
  const label = rows.length === 1 ? `Choose your ${unit}` : `Choose your ${plural}`;
  if (title.textContent !== label) title.textContent = label;
};
// Direction A uses the app's own comboboxes and image options. All selected
// glasses stay visible; the currently open native menu sits directly below
// the row being edited. No duplicate variant state or cart adapter is needed.
const revealedCutMenus = new WeakSet();
const styleImageCutTray = (bar) => {
  const root = bar.querySelector('.mz-glass-list');
  if (!root) return;
  const rows = [...root.children].filter(node => node.matches('.kaching-bundles__bar-variant-selector-wrapper'));
  const menus = [...root.querySelectorAll('[role="listbox"]')];
  const optionValues = product.options.length === 1 ? product.options[0].values.map(value => typeof value === 'string' ? value : value.name) : [];
  const photoFor = value => {
    const variant = product.variants.find(variant => variant.options?.[0] === value || variant.option1 === value);
    return variant?.featured_image?.src || variant?.featured_media?.preview_image?.src;
  };
  const applyPhoto = (control, value, fallback) => {
    const swatch = control.querySelector(':scope > .kaching-bundles__swatch');
    if (!swatch) return false;
    if (!swatch.style.getPropertyValue('--kaching-swatch-image-url') || swatch.dataset.mzFallbackPhoto) {
      const photo = photoFor(value) || fallback;
      if (photo) {
        const url = new URL(photo, location.origin);
        url.searchParams.set('width', '240');
        swatch.style.setProperty('--kaching-swatch-image-url', `url("${url.href}")`);
        swatch.dataset.mzFallbackPhoto = 'true';
      }
    }
    const ready = !!swatch.style.getPropertyValue('--kaching-swatch-image-url');
    toggleClass(swatch, 'mz-option-photo', ready);
    return ready;
  };
  rows.forEach(row => {
    const control = row.querySelector('[role="combobox"]');
    if (control) applyPhoto(control, control.closest('.kaching-bundles-option')?.dataset.value, row.querySelector('.kaching-bundles__bar-variant-image')?.src);
  });
  menus.forEach(menu => menu.querySelectorAll('[role="option"]').forEach(option => applyPhoto(option, option.dataset.value)));
  const eligible = purchase.classList.contains('mz-set-selector-ready') &&
    optionValues.length > 1 && rows.every(row => {
      const option = row.querySelector('.kaching-bundles-option');
      return option?.dataset.name === product.options[0].name && optionValues.includes(option.dataset.value) &&
        !!row.querySelector('[role="combobox"] > .mz-option-photo');
    }) && menus.every(menu => {
      const options = [...menu.querySelectorAll('[role="option"]')];
      return options.length === optionValues.length && options.every(option =>
        optionValues.includes(option.dataset.value) && !!option.querySelector('.mz-option-photo'));
    });
  toggleClass(root, 'mz-image-cut-tray', eligible);
  toggleClass(root, 'mz-paired-cut-tray', eligible);
  if (!eligible) {
    root.querySelectorAll('.mz-cut-tray__hint, .mz-cut-tray__number, .mz-cut-tray__change').forEach(node => node.remove());
    root.style.removeProperty('--mz-cut-tray-space');
    rows.forEach(row => row.style.removeProperty('--mz-inline-tray-space'));
    return;
  }
  root.dataset.mzGlassCount = String(rows.length);
  const rootWidth = root.clientWidth;
  root.style.setProperty('--mz-option-columns', optionValues.length === 6 ? 3 : Math.min(4, optionValues.length));
  toggleClass(root, 'mz-long-option-names', rootWidth < 500 && optionValues.some(value => value.length > 18));
  // Preserve readable control widths instead of squeezing six framed
  // selectors into a narrow desktop purchase column.
  root.dataset.mzCutLayout = ((rows.length === 6 && rootWidth < 660) ||
    (rows.length === 4 && rootWidth < 520)) ? 'wrapped' : 'wide';
  let hint = root.querySelector('.mz-cut-tray__hint');
  if (!hint && !root.classList.contains('mz-paired-cut-tray')) {
    hint = document.createElement('p');
    hint.className = 'mz-cut-tray__hint';
    hint.textContent = 'Tap a glass to change its cut.';
    root.querySelector('.mz-glass-list__title').after(hint);
  }
  rows.forEach((row, index) => {
    let number = row.querySelector('.mz-cut-tray__number');
    if (!number) {
      number = document.createElement('span');
      number.className = 'mz-cut-tray__number';
      row.prepend(number);
    }
    const label = `${unitTitle} ${index + 1}`;
    if (number.textContent !== label) number.textContent = label;
    const control = row.querySelector('[role="combobox"]');
    if (number.parentElement !== control) control.append(number);
    let change = control.querySelector('.mz-cut-tray__change');
    if (!change) {
      change = document.createElement('span');
      change.className = 'mz-cut-tray__change';
      change.setAttribute('aria-hidden', 'true');
      change.innerHTML = '<span>Change</span><svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m4 6 4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      control.append(change);
    }
    const action = control.getAttribute('aria-expanded') === 'true' ? 'Close' : 'Change';
    if (change.firstElementChild.textContent !== action) change.firstElementChild.textContent = action;
    const menu = row.querySelector('[role="listbox"]');
    if (menu) menu.dataset.mzGlassLabel = `${product.options[0].name} for ${unit} ${index + 1}`;
  });
  // Reserve space in the active grid row, not at the bottom of the whole set.
  // The native listbox stays in its own app subtree; only its visual position
  // changes. Later glass rows and accessories flow below the open choices.
  root.style.removeProperty('--mz-cut-tray-space');
  const openMenu = menus[0];
  const activeRow = openMenu?.closest('.kaching-bundles__bar-variant-selector-wrapper');
  const activeControl = activeRow?.querySelector('[role="combobox"]');
  if (!activeRow || !activeControl) {
    rows.forEach(row => row.style.removeProperty('--mz-inline-tray-space'));
    return;
  }
  // Removing old row reservations first keeps switching Glass 1 -> Glass 4
  // anchored to Glass 4's actual new position instead of the previous tray.
  rows.forEach(row => row.style.removeProperty('--mz-inline-tray-space'));
  const rowTop = activeRow.getBoundingClientRect().top;
  const peers = rows.filter(row => Math.abs(row.getBoundingClientRect().top - rowTop) < 2);
  const rowBottom = Math.max(...peers.map(row => row.querySelector('[role="combobox"]').getBoundingClientRect().bottom));
  const rootBounds = root.getBoundingClientRect();
  const controlBounds = activeControl.getBoundingClientRect();
  // Position does not change the full-width menu's height. Read it with the
  // other geometry before writing the tray position and row reservations.
  const space = `${Math.ceil(openMenu.getBoundingClientRect().height) + 16}px`;
  root.style.setProperty('--mz-cut-tray-top', `${Math.round(rowBottom - rootBounds.top + 16)}px`);
  root.style.setProperty('--mz-cut-tray-anchor', `${Math.round(controlBounds.left + controlBounds.width / 2 - rootBounds.left)}px`);
  peers.forEach(row => row.style.setProperty('--mz-inline-tray-space', space));
  if (!revealedCutMenus.has(openMenu)) {
    revealedCutMenus.add(openMenu);
    requestAnimationFrame(() => {
      if (!openMenu.isConnected || activeControl.getAttribute('aria-expanded') !== 'true') return;
      const cartBar = document.querySelector('.mz-quick-purchase:not([hidden])');
      const usableBottom = Math.min(window.innerHeight, cartBar?.getBoundingClientRect().top ?? window.innerHeight) - 16;
      const overlap = openMenu.getBoundingClientRect().bottom - usableBottom;
      // Reveal only the obscured part, keeping the edited glass in view.
      const shift = Math.min(overlap, Math.max(0, activeRow.getBoundingClientRect().top - 16));
      if (shift > 1) window.scrollBy({top:shift, behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    });
  }
};
if (purchase) {
  const formatter = new Intl.NumberFormat(purchase.dataset.locale || "en", {
    style: "currency",
    currency: purchase.dataset.currency || "USD",
  });
  let appMoney = null;
  const cents = node => node && appMoney ? appMoney.parse(node.textContent) : null;
  // Direction B is a presentation of the app's real offers. Its images and
  // economics come from Kaching; choosing an option activates the native bar.
  // Native style selectors, accessories, gifts and submission stay in place.
  let setSelector;
  let offerSignature = '';
  let offers = [];
  let activeOffer = 0;
  let pendingAccessories;
  let quickPurchase;
  let observedBuyButton;
  let buyButtonObserver;
  const escapeHTML = value => String(value).replace(/[&<>"']/g, char => ({
    '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;',
  }[char]));
  const money = value => appMoney ? appMoney.format(value) : formatter.format(value / 100);
  const offerPhoto = offer => {
    const image = `<img src="${escapeHTML(offer.image)}" alt="" width="112" height="112">`;
    if (offer.composed && offer.quantity > 1 && offer.quantity <= 6)
      return `<span class="mz-set-selector__photo mz-set-selector__composition" data-count="${offer.quantity}">${image.repeat(offer.quantity)}</span>`;
    return `<img class="mz-set-selector__photo" src="${escapeHTML(offer.image)}" alt="" width="112" height="112">`;
  };
  const offerContent = offer => {
    const saved = offer.original - offer.total;
    const saving = saved > 0
      ? `Save ${money(saved)} (${Math.round(saved * 100 / offer.original)}%)` : '';
    return `${offerPhoto(offer)}<span class="mz-set-selector__copy"><span class="mz-set-selector__name">${escapeHTML(offer.title)}</span><span class="mz-set-selector__prices"><span class="mz-set-selector__total">${escapeHTML(money(offer.total))}</span>${saved > 0 ? `<s>${escapeHTML(money(offer.original))}</s>` : ''}</span>${offer.quantity > 1 || glass ? `<span class="mz-set-selector__unit">${escapeHTML(money(offer.each))} / ${unit}</span>` : ''}${saving ? `<span class="mz-set-selector__saving">${escapeHTML(saving)}</span>` : ''}</span>`;
  };
  const closeOffers = () => {
    if (!setSelector) return;
    setSelector.querySelector('[role="listbox"]').hidden = true;
    const trigger = setSelector.querySelector('[role="combobox"]');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.removeAttribute('aria-activedescendant');
  };
  const highlightOffer = index => {
    activeOffer = Math.max(0, Math.min(index, offers.length - 1));
    const options = [...setSelector.querySelectorAll('[role="option"]')];
    options.forEach((option, i) => option.classList.toggle('mz-set-selector__option--focused', i === activeOffer));
    const option = options[activeOffer];
    setSelector.querySelector('[role="combobox"]').setAttribute('aria-activedescendant', option.id);
    // Scroll only the option list, never its page ancestors.
    const menu = option.parentElement;
    if (option.offsetTop < menu.scrollTop) menu.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > menu.scrollTop + menu.clientHeight)
      menu.scrollTop = option.offsetTop + option.offsetHeight - menu.clientHeight;
  };
  const openOffers = () => {
    const menu = setSelector.querySelector('[role="listbox"]');
    const trigger = setSelector.querySelector('[role="combobox"]');
    const bounds = trigger.getBoundingClientRect();
    const below = window.innerHeight - bounds.bottom - (quickPurchase?.offsetHeight || 0) - 16;
    const above = bounds.top - 16;
    menu.hidden = false;
    menu.style.top = '';
    menu.style.bottom = '';
    const wanted = Math.min(640, menu.scrollHeight + 2);
    const upward = below < wanted && above > below;
    const room = upward ? above : below;
    setSelector.dataset.placement = upward ? 'top' : 'bottom';
    if (room >= wanted) menu.style.maxHeight = wanted + 'px';
    else {
      const limit = window.innerHeight - (quickPurchase?.offsetHeight || 0) - 16;
      const height = Math.max(88, Math.min(wanted, limit - 16));
      const top = Math.max(16, Math.min(bounds.bottom + 8, limit - height));
      setSelector.dataset.placement = 'clamped';
      menu.style.top = (top - bounds.top) + 'px';
      menu.style.bottom = 'auto';
      menu.style.maxHeight = height + 'px';
    }
    trigger.setAttribute('aria-expanded', 'true');
    highlightOffer(offers.findIndex(offer => offer.bar.classList.contains('kaching-bundles__bar--selected')));
  };
  const chooseOffer = index => {
    const offer = offers[index];
    closeOffers();
    if (!offer?.bar.isConnected) return;
    if (!offer.bar.classList.contains('kaching-bundles__bar--selected')) {
      // These are shopper choices, keyed by the app's product identity. Carry
      // them through native checkbox clicks after the new offer renders.
      const previous = offers.find(item => item.bar.classList.contains('kaching-bundles__bar--selected'));
      const values = new Map();
      previous?.bar.querySelectorAll('.kaching-bundles__upsell').forEach(item => {
        const product = previous.upsells.find(upsell => upsell.id === item.dataset.upsellId)?.productGID;
        if (product) values.set(product, {
          checked:!!item.querySelector('.kaching-bundles__upsell__checkbox--selected'),
          options:[...item.querySelectorAll('select[data-name]')].map(select => ({name:select.dataset.name, value:select.value})),
        });
      });
      pendingAccessories = {id:offer.id, values};
      offer.bar.querySelector('.kaching-bundles__bar-main').click();
    }
    setSelector.querySelector('[role="combobox"]').focus({preventScroll:true});
  };
  const updateSetSelector = selected => {
    const block = selected.closest('kaching-bundles-block');
    appMoney = null;
    try { appMoney = appData.money(block, purchase.dataset.currency || 'USD'); }
    catch { /* Unknown currency configuration keeps native prices and controls. */ }
    let deal;
    try { deal = appData.json(block, 'deal-block'); } catch { /* Native fallback below. */ }
    const bars = mainBars(block);
    const next = bars.map(bar => {
      const config = deal?.dealBars?.find(item => item.id === bar.dataset.dealBarId);
      const header = bar.querySelector('.kaching-bundles__bar-main');
      const main = header?.querySelector('.kaching-bundles__bar-pricing-row--main') || header?.querySelector('.kaching-bundles__bar-pricing');
      const secondary = header?.querySelector('.kaching-bundles__bar-pricing-row--secondary');
      const pricing = secondary || main;
      const nativePhoto = header?.querySelector('.kaching-bundles__bar-image')?.src;
      const image = nativePhoto || product.image;
      const value = cents(pricing?.querySelector('.kaching-bundles__bar-price'));
      // Some widgets show per-item prices alone; only mirror a known set total.
      const total = deal?.showPricesPerItem && !secondary && config?.quantity > 1 ? null : value;
      return {bar, id:bar.dataset.dealBarId, quantity:config?.quantity,
        type:config?.dealBarType, variableQuantity:config?.quantitySelector,
        upsells:(config?.upsells || []).map(({id,productGID}) => ({id,productGID})),
        title:header?.querySelector('.kaching-bundles__bar-title')?.textContent.trim(), image, composed:!nativePhoto,
        each:total !== null && config?.quantity ? Math.round(total / config.quantity) : null,
        total, original:cents(pricing?.querySelector('.kaching-bundles__bar-full-price'))};
    });
    const current = next.find(offer => offer.bar === selected);
    const recognized = !!current && appMatchesProduct(block) && !deal?.subscriptionsEnabled && !deal?.collectionBreaksEnabled &&
      next.length > 1 && next.length === deal?.dealBars?.length && next.every(offer =>
        offer.type === 'quantity-break' && !offer.variableQuantity && Number.isInteger(offer.quantity) && offer.quantity > 0 &&
        offer.title && /^(https?:)?\/\//.test(offer.image || '') &&
        [offer.each, offer.total].every(value => Number.isInteger(value) && value >= 0));
    toggleClass(purchase, 'mz-set-selector-ready', recognized);
    toggleClass(purchase, 'mz-product-bundles', recognized);
    if (!recognized) {
      if (setSelector) { closeOffers(); setSelector.hidden = true; }
      if (quickPurchase) quickPurchase.hidden = true;
      document.body.classList.remove('mz-has-quick-purchase');
      // A completed unsupported widget keeps its native presentation. Incomplete
      // price markup is still loading; the independent bootstrap can fail open.
      const unsupported = deal?.dealBars?.length && (!appMoney || deal.subscriptionsEnabled || deal.collectionBreaksEnabled ||
        deal.dealBars.length < 2 || (deal.showPricesPerItem && !deal.showBothPrices) ||
        deal.dealBars.some(offer => offer.dealBarType !== 'quantity-break' || offer.quantitySelector));
      if (unsupported && deal.dealBars.length === bars.length && bars.every(bar =>
        bar.querySelector('.kaching-bundles__bar-price')?.textContent.trim())) revealPurchase();
      return;
    }
    offers = next;
    if (pendingAccessories?.id === current.id &&
        current.bar.querySelectorAll('.kaching-bundles__upsell').length === current.upsells.length) {
      const preferences = pendingAccessories.values;
      pendingAccessories = null;
      current.bar.querySelectorAll('.kaching-bundles__upsell').forEach(item => {
        const product = current.upsells.find(upsell => upsell.id === item.dataset.upsellId)?.productGID;
        const checkbox = item.querySelector('.kaching-bundles__upsell__checkbox');
        const preference = preferences.get(product);
        if (preference && checkbox) {
          if (checkbox.classList.contains('kaching-bundles__upsell__checkbox--selected') !== preference.checked) checkbox.click();
          for (const option of preference.options) {
            const select = [...item.querySelectorAll('select[data-name]')].find(select => select.dataset.name === option.name);
            if (select && select.value !== option.value && [...select.options].some(value => value.value === option.value && !value.disabled)) {
              select.value = option.value;
              select.dispatchEvent(new Event('change', {bubbles:true}));
            }
          }
        }
      });
    }
    if (!setSelector) {
      setSelector = document.createElement('div');
      setSelector.className = 'mz-set-selector';
      setSelector.innerHTML = '<h2 id="mz-set-selector-label">Choose your set</h2><p class="mz-set-selector__hint">Single, Duo, 4 or 6 glasses. Mix your cuts below.</p><div class="mz-set-selector__control"><button type="button" role="combobox" aria-labelledby="mz-set-selector-label" aria-haspopup="listbox" aria-controls="mz-set-selector-options" aria-expanded="false" class="mz-set-selector__trigger"></button><div id="mz-set-selector-options" role="listbox" aria-labelledby="mz-set-selector-label" class="mz-set-selector__menu" hidden></div></div>';
      setSelector.querySelector('[role="combobox"]').addEventListener('click', () => {
        if (setSelector.querySelector('[role="listbox"]').hidden) openOffers(); else closeOffers();
      });
      setSelector.querySelector('[role="combobox"]').addEventListener('keydown', event => {
        const open = !setSelector.querySelector('[role="listbox"]').hidden;
        if (['ArrowDown','ArrowUp','Home','End'].includes(event.key)) {
          event.preventDefault();
          if (!open) openOffers();
          else highlightOffer(event.key === 'Home' ? 0 : event.key === 'End' ? offers.length - 1 : activeOffer + (event.key === 'ArrowDown' ? 1 : -1));
        } else if (open && ['Enter',' '].includes(event.key)) {
          event.preventDefault(); chooseOffer(activeOffer);
        } else if (event.key === 'Escape' || event.key === 'Tab') {
          if (open && event.key === 'Escape') event.preventDefault();
          closeOffers();
        }
      });
      setSelector.querySelector('[role="listbox"]').addEventListener('click', event => {
        const option = event.target.closest('[role="option"]');
        if (option) chooseOffer(Number(option.dataset.index));
      });
      document.addEventListener('pointerdown', event => {
        if (!setSelector.contains(event.target)) closeOffers();
      });
      window.addEventListener('resize', closeOffers, {passive:true});
    }
    if (setSelector.nextElementSibling !== block) block.before(setSelector);
    setSelector.hidden = false;
    // Scope hiding to these exact main-product bars, never checkbox add-ons.
    bars.forEach(bar => toggleClass(bar, 'mz-main-offer', true));
    const signature = JSON.stringify(next.map(({bar,...values}) => values)) + current.id;
    if (offerSignature === signature) return;
    offerSignature = signature;
    closeOffers();
    const trigger = setSelector.querySelector('[role="combobox"]');
    trigger.innerHTML = offerContent(current) + '<span class="mz-set-selector__change">Change set<svg class="mz-set-selector__chevron" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"><path d="m5 7 5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></span>';
    trigger.setAttribute('aria-label', `Choose your set: ${current.title}, ${money(current.total)}`);
    trigger.removeAttribute('aria-labelledby');
    setSelector.querySelector('[role="listbox"]').innerHTML = next.map((offer,index) =>
      `<div id="mz-set-option-${index}" role="option" aria-selected="${offer.id === current.id}" data-index="${index}" class="mz-set-selector__option">${offerContent(offer)}</div>`).join('');
  };
  // This is a second access point to the existing native button, never a second
  // cart implementation. Kaching supplies the choices, gifts and line items.
  const updateQuickPurchase = ({total, saved, selected, accessoryCount}) => {
    const nativeButton = purchase.querySelector('product-form-component .add-to-cart-button');
    if (!nativeButton) {
      if (quickPurchase) quickPurchase.hidden = true;
      document.body.classList.remove('mz-has-quick-purchase');
      return;
    }
    if (!quickPurchase) {
      quickPurchase = document.createElement('section');
      quickPurchase.className = 'mz-quick-purchase';
      quickPurchase.setAttribute('aria-label', 'Your selection');
      quickPurchase.innerHTML = '<div class="mz-quick-purchase__inner"><div class="mz-quick-purchase__set"><strong></strong><span></span></div><div class="mz-quick-purchase__price"><span class="mz-quick-purchase__total"></span><span class="mz-quick-purchase__saving"></span></div><button type="button" class="mz-quick-purchase__change">Change set</button><button type="button" class="mz-quick-purchase__buy">Add to cart</button></div>';
      document.body.append(quickPurchase);
      quickPurchase.querySelector('.mz-quick-purchase__change').addEventListener('click', () => {
        const trigger = purchase.classList.contains('mz-set-selector-ready') ? setSelector.querySelector('[role="combobox"]') : purchase.querySelector('variant-picker') || nativeButton;
        trigger.scrollIntoView({block:'center', behavior:'instant'});
        trigger.focus?.({preventScroll:true});
        if (purchase.classList.contains('mz-set-selector-ready')) openOffers();
      });
      quickPurchase.querySelector('.mz-quick-purchase__buy').addEventListener('click', () => {
        const native = purchase.querySelector('product-form-component .add-to-cart-button');
        if (native && !native.disabled) native.click();
      });
      new ResizeObserver(([entry]) => {
        document.body.style.setProperty('--mz-quick-purchase-height', `${Math.ceil(entry.target.getBoundingClientRect().height)}px`);
      }).observe(quickPurchase);
    }
    const current = offers.find(offer => offer.bar === selected);
    const setName = current ? `${current.title} · ${current.quantity} ${current.quantity === 1 ? unit : plural}` : product.title;
    const subtitle = accessoryCount ? `Includes ${accessoryCount} paid ${accessoryCount === 1 ? 'extra' : 'extras'}` : '';
    const setText = quickPurchase.querySelector('.mz-quick-purchase__set');
    const updateText = (element, text) => { if (element.textContent !== text) element.textContent = text; };
    updateText(setText.querySelector('strong'), setName);
    updateText(setText.querySelector('span'), subtitle);
    quickPurchase.querySelector('.mz-quick-purchase__change').hidden = !current;
    toggleClass(quickPurchase, 'mz-quick-purchase--simple', !current);
    updateText(quickPurchase.querySelector('.mz-quick-purchase__total'), money(total));
    const saving = quickPurchase.querySelector('.mz-quick-purchase__saving');
    saving.hidden = !Number.isInteger(saved) || saved <= 0;
    updateText(saving, saved > 0 ? `You save ${money(saved)}` : '');
    const mirrorState = () => {
      const button = quickPurchase.querySelector('.mz-quick-purchase__buy');
      button.disabled = nativeButton.disabled;
      const label = nativeButton.querySelector('.add-to-cart-text__content')?.textContent.trim() || 'Add to cart';
      updateText(button, nativeButton.disabled && label === 'Add to cart' ? 'Adding…' : label);
    };
    if (observedBuyButton !== nativeButton) {
      buyButtonObserver?.disconnect();
      observedBuyButton = nativeButton;
      buyButtonObserver = new MutationObserver(mirrorState);
      buyButtonObserver.observe(nativeButton, {attributes:true, childList:true, subtree:true, characterData:true, attributeFilter:['disabled','aria-busy']});
    }
    mirrorState();
    quickPurchase.hidden = false;
    document.body.classList.add('mz-has-quick-purchase');
  };
  let scheduled = false;
  const hideMirror = () => {
    purchase.querySelector('.mz-selection-total')?.setAttribute('hidden', '');
    if (quickPurchase) quickPurchase.hidden = true;
    document.body.classList.remove('mz-has-quick-purchase');
  };
  const updateStandardPurchase = form => {
    appMoney = null;
    toggleClass(purchase, 'mz-set-selector-ready', false);
    toggleClass(purchase, 'mz-product-bundles', false);
    toggleClass(purchase, 'mz-standard-purchase', true);
    if (setSelector) { closeOffers(); setSelector.hidden = true; }
    offers = [];
    offerSignature = '';
    purchase.querySelector('.mz-selection-total')?.setAttribute('hidden', '');
    // Do not guess while an app widget initializes or for unsupported pricing.
    if (purchase.querySelector('kaching-bundles-block') || product.requiresSellingPlan || product.quantityPriceBreaks) {
      hideMirror(); return;
    }
    const variantId = form.querySelector('[name="id"]')?.value;
    const variant = product.variants.find(item => String(item.id) === variantId);
    const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
    if (!variant || !Number.isInteger(quantity) || quantity < 1 || !Number.isInteger(variant.price)) { hideMirror(); return; }
    const saved = variant.compare_at_price > variant.price ? (variant.compare_at_price - variant.price) * quantity : 0;
    updateQuickPurchase({total:variant.price * quantity, saved, selected:null, accessoryCount:0});
  };
  const update = () => {
    scheduled = false;
    const selected = mainBars(purchase.querySelector('kaching-bundles-block')).find(bar => bar.classList.contains('kaching-bundles__bar--selected'));
    const form = purchase.querySelector("product-form-component");
    if (!form) return;
    const appVariants = [...(selected?.querySelectorAll('.kaching-bundles__bar-variants :is([role="combobox"], select)') || [])]
      .some(control => !control.closest('.kaching-bundles__upsell'));
    toggleClass(purchase, 'mz-app-owns-variants', !!appVariants);
    if (!selected) { updateStandardPurchase(form); return; }
    toggleClass(purchase, 'mz-standard-purchase', false);
    updateSetSelector(selected);
    if (!purchase.classList.contains('mz-set-selector-ready')) { hideMirror(); return; }
    styleGlassList(selected);
    styleImageCutTray(selected);
    // Describe the app's current controls; Kaching still owns every selection.
    selected.querySelectorAll('.kaching-bundles__bar-variant').forEach((item, index) => {
      if (item.closest('.kaching-bundles__upsell')) return;
      item.querySelectorAll('[role="combobox"],select').forEach(control => {
        const option = control.closest('.kaching-bundles-option')?.dataset.name || 'Style';
        const label = `${option} for ${unit} ${index + 1}`;
        if (control.getAttribute('aria-label') !== label) control.setAttribute('aria-label', label);
      });
    });
    const accessories = [...selected.querySelectorAll('.kaching-bundles__upsell')];
    let otherProducts = [];
    try { otherProducts = appData.json(selected.closest('kaching-bundles-block'), 'other-products') || []; } catch { /* Keep native label. */ }
    if (purchase.classList.contains('mz-set-selector-ready')) accessories.forEach(item => {
      const checkbox = item.querySelector('.kaching-bundles__upsell__checkbox');
      const text = item.querySelector('.kaching-bundles__upsell__text');
      const label = text?.textContent.trim();
      const productGID = offers.find(offer => offer.bar === selected)?.upsells.find(upsell => upsell.id === item.dataset.upsellId)?.productGID;
      const appProduct = otherProducts.find(product => String(product.id) === productGID?.split('/').pop());
      const name = appProduct?.title || label?.replace(/^(?:\+\s*)?Add\s+(?:an?\s+)?/i, '').replace(/\s+(?:for\s+a|at)\s+[\d.]+%\s+discount.*$/i, '');
      const amount = cents(item.querySelector('.kaching-bundles__upsell__price'));
      const visibleName = name ? `${name} · paid extra` : label;
      // Include both the compact visible label and the app's original wording.
      // The native label still supplies its click handler and purchase behavior.
      const description = [visibleName, label, amount !== null ? money(amount) : null].filter(Boolean).join('. ');
      if (text && name) {
        // Keep the app's text node and click handler intact; the short visual
        // name is a presentation attribute, never another product state.
        text.dataset.mzAccessoryName = visibleName;
        text.setAttribute('aria-label', description);
      }
      const link = item.querySelector('.kaching-bundles__upsell__link');
      if (link && name) link.setAttribute('aria-label', `View ${name}`);
      if (checkbox && label) {
        if (checkbox.getAttribute('aria-label') !== description) checkbox.setAttribute('aria-label', description);
        checkbox.setAttribute('aria-pressed', String(checkbox.classList.contains('kaching-bundles__upsell__checkbox--selected')));
      }
      if (text) text.setAttribute('aria-pressed', String(!!item.querySelector('.kaching-bundles__upsell__checkbox--selected')));
    });
    if (accessories.length) {
      let heading = selected.querySelector('.mz-accessory-heading');
      if (!heading) {
        heading = document.createElement('div');
        heading.className = 'mz-accessory-heading';
        heading.innerHTML = '<h3>Optional accessories</h3><p></p>';
        accessories[0].before(heading);
      }
      if (heading.lastElementChild.textContent) heading.lastElementChild.textContent = '';
    }
    // Free benefits can be a disclosure; paid checked extras always stay visible.
    const gifts = purchase.classList.contains('mz-set-selector-ready') && purchase.querySelector('.kaching-bundles__progressive-gifts');
    if (gifts) {
      const header = gifts.querySelector('.kaching-bundles__progressive-gifts__header');
      const list = gifts.querySelector('.kaching-bundles__progressive-gifts__gifts');
      if (header && list) {
        let disclosure = header.querySelector('.mz-gift-disclosure');
        if (!disclosure) {
          disclosure = document.createElement('button');
          disclosure.type = 'button';
          disclosure.className = 'mz-gift-disclosure';
          disclosure.setAttribute('aria-expanded', 'false');
          list.id = 'mz-native-gift-list';
          disclosure.setAttribute('aria-controls', list.id);
          disclosure.innerHTML = '<span>Included with your set<small></small></span><svg aria-hidden="true" viewBox="0 0 16 16" fill="none"><path d="m4 6 4 4 4-4" stroke="currentColor" stroke-width="1.5"/></svg>';
          disclosure.addEventListener('click', () => {
            const open = disclosure.getAttribute('aria-expanded') !== 'true';
            disclosure.setAttribute('aria-expanded', String(open));
            gifts.toggleAttribute('data-mz-gifts-closed', !open);
          });
          gifts.setAttribute('data-mz-gifts-closed', '');
          gifts.classList.add('mz-compact-gifts');
          header.append(disclosure);
        }
        const unlocked = [...list.querySelectorAll('.kaching-bundles__progressive-gifts__gift:not(.kaching-bundles__progressive-gifts__gift--locked) .kaching-bundles__progressive-gifts__gift__title')].map(item => item.textContent.trim().replace('Premium Gift-Ready Packaging (Individual Wrap)', 'Individual gift packaging'));
        const locked = list.querySelectorAll('.kaching-bundles__progressive-gifts__gift--locked').length;
        const title = unlocked.length ? 'Included with your set' : 'Gifts with larger sets';
        const titleNode = disclosure.firstElementChild.firstChild;
        if (titleNode.textContent !== title) titleNode.textContent = title;
        const description = unlocked.length
          ? `${unlocked.join(' · ')}${locked ? ` · ${locked} more ${locked === 1 ? 'gift' : 'gifts'} to unlock` : ''}`
          : `${locked} gifts to unlock. See details.`;
        if (disclosure.querySelector('small').textContent !== description) disclosure.querySelector('small').textContent = description;
      }
    }
    const currentOffer = offers.find(offer => offer.bar === selected);
    const price = currentOffer?.total ?? null;
    const selectedAccessories = [...selected.querySelectorAll(".kaching-bundles__upsell")]
      .filter((item) =>
        item.querySelector(".kaching-bundles__upsell__checkbox--selected"),
      );
    const checkboxExtras = [...purchase.querySelectorAll('.kaching-bundles__checkbox-upsells__upsell')]
      .filter(item => item.querySelector('[role="checkbox"][aria-checked="true"]'));
    const extras = [
      ...selectedAccessories.map(item => cents(item.querySelector('.kaching-bundles__upsell__price'))),
      ...checkboxExtras.map(item => cents(item.querySelector('.kaching-bundles__bar-price'))),
    ];
    let output = purchase.querySelector(".mz-selection-total");
    if (price === null || extras.some((value) => value === null)) {
      if (output) output.hidden = true;
      if (quickPurchase) quickPurchase.hidden = true;
      document.body.classList.remove('mz-has-quick-purchase');
      return;
    }
    if (!output) {
      output = document.createElement("div");
      output.className = "mz-selection-total";
      output.setAttribute("aria-live", "polite");
      output.setAttribute("aria-atomic", "true");
      form.before(output);
    }
    const extra = extras.reduce((sum, value) => sum + value, 0);
    const originalPrice = currentOffer?.original ?? price;
    // Count only paid, selected items. Free gifts are not an inflated savings claim.
    const originalExtras = selectedAccessories.map((item, index) => cents(item.querySelector('.kaching-bundles__upsell__full-price')) ?? extras[index]);
    checkboxExtras.forEach(item => originalExtras.push(cents(item.querySelector('.kaching-bundles__bar-full-price')) ?? cents(item.querySelector('.kaching-bundles__bar-price'))));
    const original = originalPrice !== null ? originalPrice + originalExtras.reduce((sum, value) => sum + value, 0) : null;
    const total = price + extra;
    const saved = original !== null && original >= total ? original - total : null;
    const savings = saved > 0 ? `<div class="mz-selection-total__saving"><span>You save</span><span>${money(saved)} (${Math.round(saved * 100 / original)}%)</span></div>` : '';
    const compact = purchase.classList.contains('mz-set-selector-ready');
    toggleClass(output, 'mz-selection-total--compact', compact);
    const markup = compact
      ? `<div class="mz-selection-total__subtotal"><span>Subtotal</span><span>${money(total)}</span></div>${extras.length ? `<p class="mz-selection-total__breakdown">${glass ? 'Glasses' : 'Items'} ${money(price)} + accessories ${money(extra)}</p>` : ''}${savings}<small>Shipping and tax calculated at checkout.</small>`
      : `<div><span>Selected items</span><span>${money(price)}</span></div><div><span>Optional accessories</span><span>${money(extra)}</span></div>${savings}<div><span>Selection subtotal</span><span>${money(total)}</span></div><small>Shipping and tax calculated at checkout.</small>`;
    output.hidden = false;
    if (output.innerHTML !== markup) output.innerHTML = markup;
    updateQuickPurchase({total, saved, selected, accessoryCount:extras.length});
    revealPurchase();
  };
  new MutationObserver((records) => {
    if (
      records.every((record) => record.target.closest?.(".mz-selection-total, .mz-glass-list__title, .mz-native-choice-number, .mz-set-selector, .mz-cut-tray__hint, .mz-cut-tray__number, .mz-cut-tray__change, .mz-gift-disclosure, .mz-accessory-heading"))
    )
      return;
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(update);
    }
  }).observe(purchase, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    // Kaching can supply offer images/configuration after its price markup.
    attributeFilter: ["class", "data-value", "src", "deal-block", "config", "value", "aria-checked"],
  });
  window.addEventListener('resize', () => {
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(update);
    }
  }, {passive:true});
  const refresh = () => { if (!scheduled) { scheduled = true; requestAnimationFrame(update); } };
  purchase.addEventListener('change', refresh);
  purchase.addEventListener('input', refresh);
  document.addEventListener('variant:update', refresh);
  document.addEventListener('quantity-selector:update', refresh);
  // Keep the native server's error visible beside the floating action too.
  // This adds no cart request or client-side pricing validation.
  purchase.addEventListener('cart:error', event => {
    if (!quickPurchase || quickPurchase.hidden) return;
    let alert = quickPurchase.querySelector('.mz-quick-purchase__error');
    if (!alert) {
      alert = document.createElement('p');
      alert.className = 'mz-quick-purchase__error';
      alert.setAttribute('role', 'alert');
      quickPurchase.append(alert);
    }
    const data = event.detail?.data;
    alert.textContent = typeof data?.description === 'string' ? data.description : typeof data?.message === 'string' ? data.message : 'This selection could not be added. Please try again.';
  });
  document.addEventListener('cart:update', event => {
    if (!event.detail?.data?.didError) quickPurchase?.querySelector('.mz-quick-purchase__error')?.remove();
  });
  update();
}

})();
